import i18n from '../language-pack/index'
import reportDataService from './reportDataService'
import commonFunctions from '../mixins/commonFunctions'
import 'array-flat-polyfill'

export default class keyInsightService {
  static getSampleLevel (sampleInsight, comparisonGroupRanges) {
    const rangeHigh =
      comparisonGroupRanges && comparisonGroupRanges.rangeHigh
        ? comparisonGroupRanges.rangeHigh
        : 0
    const rangeLow =
      comparisonGroupRanges && comparisonGroupRanges.rangeLow
        ? comparisonGroupRanges.rangeLow
        : 0
    let sampleLevel = null // undetected
    if (
      (comparisonGroupRanges === null || comparisonGroupRanges === undefined) &&
      (sampleInsight === null || sampleInsight === undefined)
    ) {
      sampleLevel = '?' // undetected
    } else if (!sampleInsight) {
      sampleLevel = null // undetected
    } else if (sampleInsight.abundance > rangeHigh) {
      sampleLevel = 1 // high
    } else if (rangeLow > sampleInsight.abundance) {
      sampleLevel = -1 // low
    } else {
      sampleLevel = 0 // average
    }
    return sampleLevel
  }

  static getLevelDescriptions (
    value,
    sampleInsight,
    comparisonGroupRanges,
    isSecondary = false,
    abundanceProperty = null
  ) {
    const sampleData = {}

    // Get the actual level
    if (!sampleInsight) {
      sampleData.value = null
    } else if (typeof sampleInsight === 'object') {
      sampleData.value = sampleInsight[abundanceProperty]
    } else {
      sampleData.value = sampleInsight
    }
    sampleData.abundance = sampleData?.value
    sampleData.level = this.getSampleLevel(sampleData, comparisonGroupRanges)

    sampleData.levelReading = `${i18n.t(
      'keyInsights.generic.thisSampleReported'
    )} ${
      sampleData.level !== null && sampleData.level !== '?'
        ? i18n.t('keyInsights.generic.a')
        : i18n.t('keyInsights.generic.undetected')
    } ${i18n.t('keyInsights.generic.level')}`

    switch (sampleData.level) {
    case -1:
      sampleData.levelDescription = value.lowLevelDescription
      sampleData.levelDescriptionSpecialText =
          value.lowLevelDescriptionHighlight
      sampleData.levelReading += ` ${i18n.t('keyInsights.levels.lowerThan')} `
      sampleData.levelReadingSpecialText = ` ${i18n.t(
        'keyInsights.levels.lowerThan'
      )} `
      sampleData.shiftLevel = i18n.t('keyInsights.levels.lower')
      break
    case 0:
      sampleData.levelDescription = value.averageLevelDescription
      sampleData.levelDescriptionSpecialText =
          value.averageLevelDescriptionHighlight
      sampleData.levelReading += ` ${i18n.t('keyInsights.levels.similarTo')} `
      sampleData.levelReadingSpecialText = ` ${i18n.t(
        'keyInsights.levels.similarTo'
      )} `
      sampleData.shiftLevel = i18n.t('keyInsights.levels.similar')
      break
    case 1:
      sampleData.levelDescription = value.highLevelDescription
      sampleData.levelDescriptionSpecialText =
          value.highLevelDescriptionHighlight
      sampleData.levelReading += ` ${i18n.t(
        'keyInsights.levels.higherThan'
      )} `
      sampleData.levelReadingSpecialText = ` ${i18n.t(
        'keyInsights.levels.higherThan'
      )} `
      sampleData.shiftLevel = i18n.t('keyInsights.levels.higher')
      break
    case '?':
      sampleData.levelDescription = i18n.t(
        'details.sections.interest.notDetectedInYou'
      )
      sampleData.levelReading += ` ${i18n.t('keyInsights.levels.similarTo')} `
      sampleData.levelReadingSpecialText = ` ${i18n.t(
        'keyInsights.levels.similarTo'
      )} `
      break
    default:
      sampleData.levelDescription = i18n.t(
        'details.sections.interest.notDetectedInYou'
      )
      sampleData.levelReading += ` ${i18n.t(
        'keyInsights.levels.unlikeHealthyGroup'
      )}`
      sampleData.levelReadingSpecialText = i18n.t(
        'keyInsights.levels.undetectedLevelHighlight'
      )
      break
    }

    sampleData.levelReading +=
      sampleData.level !== null
        ? i18n.t('keyInsights.generic.theHealthyGroup')
        : ''
    sampleData.levelReadingSpecialText +=
      sampleData.level !== null
        ? i18n.t('keyInsights.generic.theHealthyGroup')
        : ''

    return sampleData
  }

  static async getMetabolitesKeyInsights (
    consolidatedPrimarySample,
    consolidatedSecondarySample,
    config
  ) {
    const hasSecondary =
      consolidatedSecondarySample &&
      consolidatedSecondarySample.identifier &&
      consolidatedSecondarySample.sampleData

    const returnData = []
    const metabolites = await reportDataService.getMetabolites(
      consolidatedPrimarySample,
      consolidatedSecondarySample,
      config
    )
    const keyInsights = this.getKeyInsightMatrixData()

    keyInsights
      .filter((insight) => insight.type === 'metabolite')
      .forEach((insightItem) => {
        // Find this insight item in the metabolites
        let metabolite = metabolites.find((metabolite) => metabolite.uuid === insightItem.uuid)
        if (!metabolite || !metabolite.uuid) {
          console.error(`This metabolite not found: ${insightItem.uuid}`)
          metabolite = {
            uuid: '0000',
            currentValue: 0,
            category: 0,
            direction: 0,
            name: 'Missing metabolite',
            max: 0,
            min: 0,
            rangeLow: 0,
            rangeHigh: 0
          }
        }

        // Set the default category needed for autoscroll
        insightItem.defaultCategory = metabolite.category

        const primaryMetaboliteInsight = {
          uuid: metabolite.uuid,
          abundance: metabolite.currentValue || 0,
          category: metabolite.category,
          direction: metabolite.direction,
          name: metabolite.name
        }

        const comparisonGroupRanges = {
          uuid: metabolite.uuid,
          max: metabolite.max,
          min: metabolite.min,
          rangeLow: metabolite.range_low,
          rangeHigh: metabolite.range_high
        }

        if (primaryMetaboliteInsight) {
          insightItem.primary = this.getLevelDescriptions(
            insightItem,
            primaryMetaboliteInsight,
            comparisonGroupRanges,
            false,
            'abundance'
          )
          insightItem.levelIndicatorPositive =
            insightItem.isBad.indexOf(insightItem.primary.level) === -1 // Not found in the bad list is a positive thing.
          insightItem.levelDescription = insightItem.primary.levelDescription
          insightItem.levelDescriptionHighlight =
            insightItem.primary.levelDescriptionSpecialText
        }

        if (hasSecondary) {
          const secondaryMetaboliteInsight = {
            uuid: metabolite.uuid,
            abundance: metabolite.secondaryValue || 0,
            category: metabolite.category,
            direction: metabolite.direction,
            name: metabolite.name
          }

          if (secondaryMetaboliteInsight) {
            insightItem.secondary = this.getLevelDescriptions(
              insightItem,
              secondaryMetaboliteInsight,
              comparisonGroupRanges,
              true,
              'abundance'
            )
            // Detect shifts
            insightItem.levelIndicatorPositive =
              insightItem.isBad.indexOf(insightItem.secondary.level) === -1 // Not found in the bad list is a positive thing.
            insightItem.levelDescription =
              insightItem[
                insightItem.primary.shiftLevel +
                  insightItem.secondary.shiftLevel
              ]
            insightItem.levelDescriptionHighlight = `${
              insightItem[
                insightItem.primary.shiftLevel +
                  insightItem.secondary.shiftLevel +
                  'Highlight'
              ]
            }`
          }
        }

        returnData.push(insightItem)
      })
    return returnData
  }

  static getMacronutrientsKeyInsights (
    consolidatedPrimarySample,
    consolidatedSecondarySample
  ) {
    const hasSecondary =
      consolidatedSecondarySample &&
      consolidatedSecondarySample.identifier &&
      consolidatedSecondarySample.sampleData

    const returnData = []
    const keyInsights = this.getKeyInsightMatrixData()
    const comparisonKeyInsightData =
      consolidatedPrimarySample.healthyCohortData.macronutrientProfile || []
    const primarySampleMacronutrientData =
      consolidatedPrimarySample.sampleData.macronutrientProfile || []
    const secondarySampleMacronutrientData =
      (hasSecondary &&
        consolidatedSecondarySample.sampleData.macronutrientProfile) ||
      []

    keyInsights
      .filter((insight) => insight.type === 'macronutrient')
      .forEach((macronutrientItem) => {
        macronutrientItem.primary = this.getLevelDescriptions(
          macronutrientItem,
          primarySampleMacronutrientData[macronutrientItem.uuid.toLowerCase()],
          comparisonKeyInsightData[macronutrientItem.uuid.toLowerCase()],
          false
        )

        // Set the default category needed for autoscroll
        macronutrientItem.defaultCategory = macronutrientItem.uuid

        macronutrientItem.levelIndicatorPositive =
          macronutrientItem.isBad.indexOf(macronutrientItem.primary.level) ===
          -1 // Not found in the bad list is a positive thing.
        macronutrientItem.levelDescription =
          macronutrientItem.primary.levelDescription
        macronutrientItem.levelDescriptionHighlight =
          macronutrientItem.primary.levelDescriptionSpecialText
        if (hasSecondary) {
          macronutrientItem.secondary = this.getLevelDescriptions(
            macronutrientItem,
            secondarySampleMacronutrientData[
              macronutrientItem.uuid.toLowerCase()
            ],
            comparisonKeyInsightData[macronutrientItem.uuid.toLowerCase()],
            true
          )

          // Detect shifts
          macronutrientItem.levelIndicatorPositive =
            macronutrientItem.isBad.indexOf(
              macronutrientItem.secondary.level
            ) === -1 // Not found in the bad list is a positive thing.
          macronutrientItem.levelDescription =
            macronutrientItem[
              macronutrientItem.primary.shiftLevel +
                macronutrientItem.secondary.shiftLevel
            ]
          macronutrientItem.levelDescriptionHighlight = `${
            macronutrientItem[
              macronutrientItem.primary.shiftLevel +
                macronutrientItem.secondary.shiftLevel +
                'Highlight'
            ]
          }`
        }
        returnData.push(macronutrientItem)
      })

    return returnData
  }

  static async getKeyInsightData (
    consolidatedPrimarySample = null,
    consolidatedSecondarySample = null,
    config = null
  ) {
    if (!consolidatedPrimarySample) throw new Error('primarySample is missing.')
    let returnData = []

    const metaboliteInsights = await this.getMetabolitesKeyInsights(
      consolidatedPrimarySample,
      consolidatedSecondarySample,
      config
    )
    returnData = [...returnData, ...metaboliteInsights]

    const macronutrientInsights = this.getMacronutrientsKeyInsights(
      consolidatedPrimarySample,
      consolidatedSecondarySample
    )
    returnData = [...returnData, ...macronutrientInsights]

    return returnData
  }

  static getEvidenceRating () {
    return Object.freeze({
      title: i18n.t('keyInsights.evidenceRatings.title'),
      description: i18n.t('keyInsights.evidenceRatings.description'),
      referenceLink:
        '<a href="https://www.cebm.net/wp-content/uploads/2014/06/CEBM-Levels-of-Evidence-2.1.pdf" target="_new"><sup>1</sup></a>'
    })
  }

  static getKeyInsightMatrixData () {
    const keyInsightsArray = commonFunctions.computed?.reportEntity()?.reportConfiguration?.keyInsights || []

    // Complete the list with extra info
    keyInsightsArray.forEach((keyInsight) => {
      const key = keyInsight.key

      // Some general additions
      keyInsight.title = i18n.t(`keyInsights.${key}.title`, {
        class: 'special'
      })
      keyInsight.description = i18n.t(`keyInsights.${key}.description`)
      keyInsight.chartLocation = keyInsight.relatedChart

      // Add the lower-higher descriptions
      keyInsight.lowLevelDescription = i18n.t(
        `keyInsights.${key}.lowLevelDescription`
      )
      keyInsight.lowLevelDescriptionHighlight = i18n.t(
        `keyInsights.${key}.lowLevelDescriptionHighlight`
      )
      keyInsight.averageLevelDescription = i18n.t(
        `keyInsights.${key}.averageLevelDescription`
      )
      keyInsight.averageLevelDescriptionHighlight = i18n.t(
        `keyInsights.${key}.averageLevelDescriptionHighlight`
      )
      keyInsight.highLevelDescription = i18n.t(
        `keyInsights.${key}.highLevelDescription`
      )
      keyInsight.highLevelDescriptionHighlight = i18n.t(
        `keyInsights.${key}.highLevelDescriptionHighlight`
      )
      keyInsight.lowerlower = i18n.t(`keyInsights.${key}.lowerlower`)
      keyInsight.lowerlowerHighlight = i18n.t(
        `keyInsights.${key}.lowerlowerHighlight`
      )
      keyInsight.lowersimilar = i18n.t(`keyInsights.${key}.lowersimilar`)
      keyInsight.lowersimilarHighlight = i18n.t(
        `keyInsights.${key}.lowersimilarHighlight`
      )
      keyInsight.lowerhigher = i18n.t(`keyInsights.${key}.lowerhigher`)
      keyInsight.lowerhigherHighlight = i18n.t(
        `keyInsights.${key}.lowerhigherHighlight`
      )
      keyInsight.similarlower = i18n.t(`keyInsights.${key}.similarlower`)
      keyInsight.similarlowerHighlight = i18n.t(
        `keyInsights.${key}.similarlowerHighlight`
      )
      keyInsight.similarsimilar = i18n.t(`keyInsights.${key}.similarsimilar`)
      keyInsight.similarsimilarHighlight = i18n.t(
        `keyInsights.${key}.similarsimilarHighlight`
      )
      keyInsight.similarhigher = i18n.t(`keyInsights.${key}.similarhigher`)
      keyInsight.similarhigherHighlight = i18n.t(
        `keyInsights.${key}.similarhigherHighlight`
      )
      keyInsight.higherlower = i18n.t(`keyInsights.${key}.higherlower`)
      keyInsight.higherlowerHighlight = i18n.t(
        `keyInsights.${key}.higherlowerHighlight`
      )
      keyInsight.highersimilar = i18n.t(`keyInsights.${key}.highersimilar`)
      keyInsight.highersimilarHighlight = i18n.t(
        `keyInsights.${key}.highersimilarHighlight`
      )
      keyInsight.higherhigher = i18n.t(`keyInsights.${key}.higherhigher`)
      keyInsight.higherhigherHighlight = i18n.t(
        `keyInsights.${key}.higherhigherHighlight`
      )
    })

    return keyInsightsArray
  }
}
